import { useEffect, useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
} from "recharts";
import { Calendar, RefreshCw, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}
function isoDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

const PRESETS = [
  { id: "7", label: "Laatste 7 dagen", from: () => isoDaysAgo(7), to: todayIso, group: "day" },
  { id: "30", label: "Laatste 30 dagen", from: () => isoDaysAgo(30), to: todayIso, group: "day" },
  { id: "90", label: "Laatste 90 dagen", from: () => isoDaysAgo(90), to: todayIso, group: "week" },
  { id: "ytd", label: "Dit jaar (YTD)", from: () => `${new Date().getFullYear()}-01-01`, to: todayIso, group: "month" },
  { id: "all", label: "Alles", from: () => "2025-01-01", to: todayIso, group: "month" },
];

function StatCard({ label, value, sub, testId }) {
  return (
    <div data-testid={testId} className="bg-white border border-oc-border rounded-oc p-6">
      <p className="text-[12px] uppercase tracking-[0.08em] font-semibold text-oc-textSecondary">{label}</p>
      <p className="font-mono text-oc-primary font-semibold mt-2 text-[26px] leading-none">{value}</p>
      {sub && <p className="text-oc-textSecondary text-[12px] mt-1">{sub}</p>}
    </div>
  );
}

function fmt(v) {
  return `€ ${Number(v || 0).toLocaleString("nl-BE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function AdminRevenue() {
  const [from, setFrom] = useState(isoDaysAgo(30));
  const [to, setTo] = useState(todayIso());
  const [groupBy, setGroupBy] = useState("day");
  const [vatRate, setVatRate] = useState(0.21);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        date_from: from,
        date_to: to,
        group_by: groupBy,
        vat_rate: String(vatRate),
      });
      const { data } = await api.get(`/admin/revenue?${params.toString()}`);
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const applyPreset = (preset) => {
    setFrom(preset.from());
    setTo(preset.to());
    setGroupBy(preset.group);
  };

  const exportCsv = () => {
    if (!data) return;
    const rows = [["periode", "aantal", "excl_btw", "btw", "incl_btw"]];
    data.buckets.forEach((b) => rows.push([b.period, b.count, b.excl_vat, b.vat, b.incl_vat]));
    rows.push(["TOTAAL", data.totals.count, data.totals.excl_vat, data.totals.vat, data.totals.incl_vat]);
    const csv = rows.map((r) => r.map((v) => `"${String(v).replaceAll('"', '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `omzet-${from}-tot-${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totals = data?.totals || { incl_vat: 0, excl_vat: 0, vat: 0, count: 0 };
  const avg = totals.count > 0 ? totals.incl_vat / totals.count : 0;

  return (
    <div className="space-y-7">
      <div>
        <h1 className="font-display font-bold text-oc-primary text-[34px] flex items-center gap-3">
          <Calendar className="w-7 h-7 text-oc-secondary" /> Omzet
        </h1>
        <p className="text-oc-textSecondary mt-1">Omzet per dag, week, maand of jaar — met of zonder BTW.</p>
      </div>

      <div className="bg-white border border-oc-border rounded-oc p-7 space-y-5">
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <Button
              key={p.id}
              variant="outline"
              size="sm"
              onClick={() => applyPreset(p)}
              className="rounded-full border-oc-border text-oc-textSecondary hover:bg-oc-subtle hover:text-oc-primary text-[13px]"
            >
              {p.label}
            </Button>
          ))}
        </div>

        <div className="grid sm:grid-cols-4 gap-3 items-end">
          <div className="space-y-2">
            <Label>Vanaf</Label>
            <Input data-testid="rev-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-12 rounded-oc-btn" />
          </div>
          <div className="space-y-2">
            <Label>Tot</Label>
            <Input data-testid="rev-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-12 rounded-oc-btn" />
          </div>
          <div className="space-y-2">
            <Label>Groepering</Label>
            <Select value={groupBy} onValueChange={setGroupBy}>
              <SelectTrigger data-testid="rev-group" className="h-12 rounded-oc-btn">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="day">Per dag</SelectItem>
                <SelectItem value="week">Per week</SelectItem>
                <SelectItem value="month">Per maand</SelectItem>
                <SelectItem value="year">Per jaar</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>BTW-tarief</Label>
            <Select value={String(vatRate)} onValueChange={(v) => setVatRate(parseFloat(v))}>
              <SelectTrigger className="h-12 rounded-oc-btn">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0.21">21% (standaard)</SelectItem>
                <SelectItem value="0.12">12%</SelectItem>
                <SelectItem value="0.06">6%</SelectItem>
                <SelectItem value="0">0% (geen BTW)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button data-testid="rev-load" onClick={load} disabled={loading} className="bg-oc-primary hover:bg-oc-secondary text-white rounded-oc-btn h-11 px-5 font-semibold">
            <RefreshCw className="w-4 h-4 mr-2" /> Toon resultaat
          </Button>
          <Button variant="outline" onClick={exportCsv} disabled={!data} className="rounded-oc-btn h-11 border-oc-border text-oc-primary hover:bg-oc-subtle">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      {data && (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard testId="rev-total-incl" label="Totaal incl. BTW" value={fmt(totals.incl_vat)} sub={`${totals.count} transacties`} />
            <StatCard testId="rev-total-excl" label="Totaal excl. BTW" value={fmt(totals.excl_vat)} sub={`@ ${(vatRate * 100).toFixed(0)}% BTW`} />
            <StatCard testId="rev-vat" label="Te betalen BTW" value={fmt(totals.vat)} sub="Aftrekbaar voor klant" />
            <StatCard label="Gem. transactie" value={fmt(avg)} sub="incl. BTW" />
          </div>

          <div className="bg-white border border-oc-border rounded-oc p-7">
            <h3 className="font-display font-semibold text-oc-primary text-[18px]">Omzet per {groupBy === "day" ? "dag" : groupBy === "week" ? "week" : groupBy === "month" ? "maand" : "jaar"}</h3>
            <div className="h-80 mt-5">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.buckets} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
                  <XAxis dataKey="period" stroke="#475569" fontSize={11} />
                  <YAxis stroke="#475569" fontSize={11} tickFormatter={(v) => `€${v}`} />
                  <Tooltip
                    contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12 }}
                    formatter={(v) => fmt(v)}
                  />
                  <Legend />
                  <Bar dataKey="excl_vat" name="Excl. BTW" stackId="a" fill="#1B4B8A" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="vat" name="BTW" stackId="a" fill="#2563EB" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white border border-oc-border rounded-oc overflow-hidden">
            <div className="grid grid-cols-5 px-6 py-3 bg-oc-subtle text-[12px] uppercase tracking-[0.05em] font-semibold text-oc-textSecondary">
              <span>Periode</span>
              <span className="text-right">Aantal</span>
              <span className="text-right">Excl. BTW</span>
              <span className="text-right">BTW</span>
              <span className="text-right">Incl. BTW</span>
            </div>
            <div className="divide-y divide-oc-border">
              {data.buckets.map((b) => (
                <div key={b.period} className="grid grid-cols-5 px-6 py-3 text-[14px]">
                  <span className="text-oc-primary font-medium">{b.period}</span>
                  <span className="text-right font-mono">{b.count}</span>
                  <span className="text-right font-mono">{fmt(b.excl_vat)}</span>
                  <span className="text-right font-mono text-oc-textSecondary">{fmt(b.vat)}</span>
                  <span className="text-right font-mono font-semibold text-oc-primary">{fmt(b.incl_vat)}</span>
                </div>
              ))}
              {data.buckets.length === 0 && (
                <p className="px-6 py-10 text-center text-oc-textSecondary">Geen omzet in deze periode</p>
              )}
            </div>
            {data.buckets.length > 0 && (
              <div className="grid grid-cols-5 px-6 py-4 bg-oc-primary text-white text-[14px] font-semibold">
                <span>Totaal</span>
                <span className="text-right font-mono">{totals.count}</span>
                <span className="text-right font-mono">{fmt(totals.excl_vat)}</span>
                <span className="text-right font-mono">{fmt(totals.vat)}</span>
                <span className="text-right font-mono">{fmt(totals.incl_vat)}</span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
